import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Plus, Filter, Download, Eye, Edit, Search, Building2, Trash2, X, Save, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
const initialStudiesData = Array.from({
  length: 50
}, (_, i) => ({
  id: i + 1,
  name: i % 2 === 0 ? `Studio Commerciale ${i}` : `Studio Lavoro ${i}`,
  type: i % 3 === 0 ? 'Commercialista' : i % 3 === 1 ? 'Consulente del Lavoro' : 'Altro',
  city: i % 3 === 0 ? 'Arezzo' : 'Firenze',
  email: `info@studio${i}.it`,
  phone: `0575 ${200000 + i}`
}));
const columnsConfig = [{
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'city',
  label: 'Città'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'phone',
  label: 'Telefono'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function StudiesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialStudiesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'email']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({});
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Studio eliminato', 'Lo studio è stato eliminato con successo.');
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Studio salvato', 'Lo studio è stato salvato con successo.');
    }, 2000);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-2623">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2624">
        <div data-id="element-2625">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2626">
            Anagrafica studi
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2627">
            Gestione partner esterni (Commercialisti, Consulenti del Lavoro).
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2628">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2630" />} data-id="element-2629">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2632" />} data-id="element-2631">
            Nuovo studio
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-2633">
        <div className="p-4 flex flex-col gap-4" data-id="element-2634">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2635">
            <div className="flex-1" data-id="element-2636">
              <Input placeholder="Cerca studio..." startContent={<Search className="h-4 w-4" data-id="element-2638" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2637" />
            </div>
            <div className="flex gap-2" data-id="element-2639">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2641" />} data-id="element-2640">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2642">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="studies" data-id="element-2643" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2644">
              <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Commercialista',
            label: 'Commercialista'
          }, {
            value: 'Consulente del Lavoro',
            label: 'Consulente del Lavoro'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-2645" />
              <div className="col-span-full flex justify-end" data-id="element-2646">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2648" />} data-id="element-2647">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-2649">
        <div className="overflow-x-auto" data-id="element-2650">
          <table className="w-full text-left border-collapse" data-id="element-2651">
            <thead data-id="element-2652">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2653">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-left w-32' : ''}`} data-id="element-2654">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2655">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2656">
                  {visibleColumns.includes('name') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2657">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2658">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('city') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2659">
                      {item.city}
                    </td>}
                  {visibleColumns.includes('email') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2660">
                      {item.email}
                    </td>}
                  {visibleColumns.includes('phone') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2661">
                      {item.phone}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4" data-id="element-2662">
                      <div className="flex items-center gap-2" data-id="element-2663">
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} title="Visualizza studio" data-id="element-2664">
                          <Eye className="h-4 w-4 text-slate-500" data-id="element-2665" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} title="Modifica studio" data-id="element-2666">
                          <Edit className="h-4 w-4 text-seform-orange" data-id="element-2667" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} title="Elimina studio" data-id="element-2668">
                          <Trash2 className="h-4 w-4 text-red-500" data-id="element-2669" />
                        </Button>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-2670">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2671" />
        </div>
      </Card>

      {/* View Modal (Consultation) */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio studio" size="lg" data-id="element-2672">
        {selectedItem && <div className="space-y-6" data-id="element-2673">
            <div className="grid grid-cols-2 gap-4" data-id="element-2674">
              <div data-id="element-2675">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2676">
                  Ragione sociale
                </span>
                <p className="font-medium" data-id="element-2677">{selectedItem.name}</p>
              </div>
              <div data-id="element-2678">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2679">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-2680">{selectedItem.type}</p>
              </div>
              <div data-id="element-2681">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2682">
                  Città
                </span>
                <p className="font-medium" data-id="element-2683">{selectedItem.city}</p>
              </div>
              <div data-id="element-2684">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2685">
                  Email
                </span>
                <p className="font-medium" data-id="element-2686">{selectedItem.email}</p>
              </div>
              <div data-id="element-2687">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2688">
                  Telefono
                </span>
                <p className="font-medium" data-id="element-2689">{selectedItem.phone}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2690">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-2691">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-2693" />} data-id="element-2692">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica studio' : 'Nuovo studio'} size="2xl" data-id="element-2694">
        <AnimatePresence mode="wait" data-id="element-2695">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2696">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2697">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2698" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2699">
                Studio salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2700">
              <div className="space-y-8" data-id="element-2701">
                <section data-id="element-2702">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2703">
                    <Building2 className="h-4 w-4" data-id="element-2704" /> Dettagli studio
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2705">
                    <Input label="Ragione sociale" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-2706" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Commercialista',
                  label: 'Commercialista'
                }, {
                  value: 'Consulente del Lavoro',
                  label: 'Consulente del Lavoro'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} required data-id="element-2707" />
                    <Input label="Città" value={formData.city || ''} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} data-id="element-2708" />
                    <Input label="Email" type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-2709" />
                    <Input label="Telefono" type="tel" value={formData.phone || ''} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} data-id="element-2710" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2711">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2712">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2714" />} data-id="element-2713">
                    Salva studio
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-2715">
        <AnimatePresence mode="wait" data-id="element-2716">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-2717">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2718">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-2719" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2720">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2721">
              <div className="space-y-6" data-id="element-2722">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-2723">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-2724" />
                  <p className="text-sm text-red-700" data-id="element-2725">
                    Attenzione: questa azione è irreversibile. Lo studio verrà
                    eliminato definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-2726">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-2727">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-2728">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}